import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  FiHeart,
  FiShoppingCart,
  FiEye,
  FiX,
  FiShoppingBag,
  FiPlus,
  FiMinus,
} from "react-icons/fi";
import { useAppContext } from "../AppContext";
import { MyDomain } from "../App";

function getGenderInfo(gender) {
  switch ((gender || "").toLowerCase()) {
    case "male":
    case "men":    return { symbol: "♂", label: "Men",    color: "#3b82f6", bg: "rgba(59,130,246,0.1)",  border: "rgba(59,130,246,0.3)"  };
    case "female":
    case "women":  return { symbol: "♀", label: "Women",  color: "#ec4899", bg: "rgba(236,72,153,0.1)",  border: "rgba(236,72,153,0.3)"  };
    default:       return { symbol: "⚥", label: "Unisex", color: "#8b5cf6", bg: "rgba(139,92,246,0.1)", border: "rgba(139,92,246,0.3)" };
  }
}

function GenderBadge({ gender, style = {} }) {
  if (!gender) return null;
  const g = getGenderInfo(gender);
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        background: g.bg,
        color: g.color,
        fontSize: 11,
        fontWeight: 700,
        padding: "2px 8px",
        borderRadius: 20,
        border: `1px solid ${g.border}`,
        letterSpacing: 0.3,
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      <span style={{ fontSize: 13, lineHeight: 1 }}>{g.symbol}</span>
      {g.label}
    </span>
  );
}

function QtyRow({ amount, onIncrease, onDecrease, size = "md" }) {
  const isSmall = size === "sm";
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: isSmall ? 0 : 2,
        background: "rgba(255,255,255,0.15)",
        borderRadius: 30,
        overflow: "hidden",
      }}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onDecrease(); }}
        style={{
          background: "rgba(255,255,255,0.2)",
          border: "none",
          color: "#fff",
          cursor: "pointer",
          width: isSmall ? 28 : 34,
          height: isSmall ? 28 : 34,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "50%",
          flexShrink: 0,
          transition: "background 0.2s",
        }}
      >
        <FiMinus size={isSmall ? 12 : 14} />
      </button>
      <span
        style={{
          color: "#fff",
          fontWeight: 700,
          fontSize: isSmall ? 13 : 15,
          minWidth: isSmall ? 22 : 28,
          textAlign: "center",
        }}
      >
        {amount}
      </span>
      <button
        onClick={(e) => { e.stopPropagation(); onIncrease(); }}
        style={{
          background: "rgba(255,255,255,0.2)",
          border: "none",
          color: "#fff",
          cursor: "pointer",
          width: isSmall ? 28 : 34,
          height: isSmall ? 28 : 34,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "50%",
          flexShrink: 0,
          transition: "background 0.2s",
        }}
      >
        <FiPlus size={isSmall ? 12 : 14} />
      </button>
    </div>
  );
}

function ProductModal({ product, onClose, onViewProduct, cartAmount, onAddToCart, onUpdateCart }) {
  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const inCart = cartAmount > 0;

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: 20,
        backdropFilter: "blur(3px)",
        animation: "fadeIn 0.2s ease",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#ffffff",
          borderRadius: 18,
          overflow: "hidden",
          width: "100%",
          maxWidth: 480,
          boxShadow: "0 24px 60px rgba(0,0,0,0.25)",
          animation: "slideUp 0.25s ease",
          position: "relative",
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: "absolute", top: 12, right: 12, zIndex: 10,
            background: "rgba(255,255,255,0.9)", border: "1px solid rgba(0,0,0,0.1)",
            borderRadius: "50%", width: 34, height: 34, display: "flex",
            alignItems: "center", justifyContent: "center", cursor: "pointer",
            color: "#333", boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <FiX size={16} />
        </button>

        <div style={{ position: "relative" }}>
          <img
            src={product.image}
            alt={product.name}
            onError={(e) => { e.target.src = "https://placehold.co/400x400/f5f5f5/ccc?text=No+Image"; }}
            style={{ width: "100%", aspectRatio: "1 / 1", objectFit: "contain", display: "block", background: "#f9f9f9" }}
          />
          {product.hasDiscount && (
            <span style={{ position: "absolute", top: 12, left: 12, background: "#e11d48", color: "white", fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20 }}>
              -{product.discountPercent}%
            </span>
          )}
          {product.isNew && (
            <span style={{ position: "absolute", top: 12, right: 12, background: "#f59e0b", color: "#fff", fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20 }}>
              NEW
            </span>
          )}
        </div>

        <div style={{ padding: "20px 22px 24px" }}>
          <p style={{ color: "#1a1a1a", fontSize: 16, fontWeight: 600, margin: "0 0 8px", lineHeight: 1.4 }}>
            {product.name}
          </p>
          <GenderBadge gender={product.gender} style={{ marginBottom: 10 }} />
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: product.deal ? 10 : 20 }}>
            <span style={{ color: "#1a1a1a", fontSize: 20, fontWeight: 700 }}>₪{product.price}</span>
            {product.hasDiscount && product.originalPrice && (
              <span style={{ color: "#bbb", fontSize: 14, textDecoration: "line-through" }}>₪{product.originalPrice}</span>
            )}
          </div>
          {product.deal && (
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 20 }}>
              <span style={{ background: "#7c3aed", color: "#fff", fontSize: 12, fontWeight: 700, padding: "4px 12px", borderRadius: 20, whiteSpace: "nowrap" }}>
                {product.deal}
              </span>
            </div>
          )}

          <div style={{ display: "flex", gap: 10 }}>
            {inCart ? (
              <div
                style={{
                  flex: 1, background: "#e11d48", borderRadius: 10, padding: "10px 0",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
                }}
              >
                <QtyRow
                  amount={cartAmount}
                  onIncrease={() => onUpdateCart(cartAmount + 1)}
                  onDecrease={() => onUpdateCart(cartAmount - 1)}
                />
              </div>
            ) : (
              <button
                onClick={onAddToCart}
                style={{
                  flex: 1, background: "#e11d48", color: "white", border: "none",
                  borderRadius: 10, padding: "12px 0", fontSize: 14, fontWeight: 600,
                  cursor: "pointer", display: "flex", alignItems: "center",
                  justifyContent: "center", gap: 8,
                }}
              >
                <FiShoppingCart size={16} />
                Add to Cart
              </button>
            )}
            <button
              onClick={onViewProduct}
              style={{
                flex: 1, background: "transparent", color: "#1a1a1a",
                border: "1.5px solid rgba(0,0,0,0.15)", borderRadius: 10, padding: "12px 0",
                fontSize: 14, fontWeight: 600, cursor: "pointer", display: "flex",
                alignItems: "center", justifyContent: "center", gap: 8,
              }}
            >
              <FiShoppingBag size={16} />
              View Product
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn  { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>
    </div>,
    document.body,
  );
}

function IconBtn({ onClick, hoverColor = "rgba(0,0,0,0.04)", children }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        flexShrink: 0,
        background: hovered ? hoverColor : "rgba(0,0,0,0.04)",
        border: "1px solid rgba(0,0,0,0.1)",
        borderRadius: "50%",
        width: 32, height: 32,
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer", color: "#999", transition: "all 0.2s",
      }}
    >
      {children}
    </button>
  );
}

export default function ProductCard({ product }) {
  const [hovered, setHovered] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [loginWarning, setLoginWarning] = useState(false);
  const navigate = useNavigate();
  const { User, favIds, setFavIds, setFavCount, cartItems, setCartItems } = useAppContext();

  const favorited = favIds.has(product.id);
  const cartAmount = cartItems[product.id] || 0;
  const inCart = cartAmount > 0;

  const showLoginWarning = () => {
    setLoginWarning(true);
    setTimeout(() => setLoginWarning(false), 2500);
  };

  const handleFavoriteClick = async (e) => {
    e.stopPropagation();
    if (!User) { showLoginWarning(); return; }
    try {
      if (!favorited) {
        await fetch(`${MyDomain}Products2/AddToFavorites`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId: User.id, productId: product.id }),
        });
        setFavIds((prev) => new Set([...prev, product.id]));
        setFavCount((c) => c + 1);
      } else {
        await fetch(`${MyDomain}Products2/DeleteFromFavorites`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId: User.id, productId: product.id }),
        });
        setFavIds((prev) => { const n = new Set(prev); n.delete(product.id); return n; });
        setFavCount((c) => Math.max(0, c - 1));
      }
    } catch (err) { console.error("Favorite action failed", err); }
  };

  const handleAddToCart = async (e) => {
    e?.stopPropagation();
    if (!User) { showLoginWarning(); return; }
    try {
      await fetch(`${MyDomain}Products2/AddToCart`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: User.id, productId: product.id }),
      });
      setCartItems((prev) => ({ ...prev, [product.id]: (prev[product.id] || 0) + 1 }));
    } catch (err) { console.error("Add to cart failed", err); }
  };

  const handleUpdateCart = async (newAmt) => {
    if (!User) return;
    try {
      if (newAmt <= 0) {
        await fetch(`${MyDomain}Products2/RemoveFromCart`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId: User.id, productId: product.id }),
        });
        setCartItems((prev) => { const n = { ...prev }; delete n[product.id]; return n; });
      } else {
        await fetch(`${MyDomain}Products2/UpdateCartAmount`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId: User.id, productId: product.id, amount: newAmt }),
        });
        setCartItems((prev) => ({ ...prev, [product.id]: newAmt }));
      }
    } catch (err) { console.error("Cart update failed", err); }
  };

  return (
    <>
      <div className="product-card-outer" style={{ padding: "0 10px 4px" }}>
        <div
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={{
            background: "#ffffff",
            borderRadius: 14,
            overflow: "hidden",
            border: "1px solid rgba(0,0,0,0.08)",
            cursor: "pointer",
            transition: "transform 0.25s, box-shadow 0.25s",
            transform: hovered ? "translateY(-5px)" : "translateY(0)",
            boxShadow: hovered ? "0 16px 40px rgba(0,0,0,0.13)" : "0 2px 8px rgba(0,0,0,0.07)",
          }}
        >
          {/* ── Image block ── */}
          <div style={{ position: "relative", overflow: "hidden" }}>
            <img
              src={product.image}
              alt={product.name}
              onClick={() => navigate(`/product/${product.id}`)}
              onError={(e) => { e.target.src = "https://placehold.co/400x400/f5f5f5/ccc?text=No+Image"; }}
              style={{
                width: "100%", aspectRatio: "1 / 1", objectFit: "contain",
                display: "block", background: "#f9f9f9",
                transition: "transform 0.45s",
                transform: hovered ? "scale(1.07)" : "scale(1)",
                cursor: "pointer",
              }}
            />

            <div
              style={{
                position: "absolute", inset: 0,
                background: "rgba(0,0,0,0.18)",
                opacity: hovered ? 1 : 0,
                transition: "opacity 0.3s",
                pointerEvents: "none",
              }}
            />

            {/* SALE badge */}
            {product.hasDiscount && (
              <span style={{ position: "absolute", top: 10, left: 10, background: "#e11d48", color: "white", fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 20, letterSpacing: 0.4, lineHeight: 1.5 }}>
                -{product.discountPercent}%
              </span>
            )}

            {/* NEW badge */}
            {product.isNew && (
              <span style={{ position: "absolute", top: 10, right: 10, background: "#f59e0b", color: "#fff", fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 20, letterSpacing: 0.4, lineHeight: 1.5 }}>
                NEW
              </span>
            )}

            {/* Cart bar — slides up on hover, stays visible when in cart */}
            <div
              style={{
                position: "absolute", bottom: 0, left: 0, right: 0,
                background: "#e11d48",
                display: "flex", alignItems: "center", justifyContent: "center",
                gap: 8, padding: "11px 0",
                transform: (inCart || hovered) ? "translateY(0)" : "translateY(100%)",
                transition: "transform 0.3s cubic-bezier(0.4,0,0.2,1)",
                zIndex: 2,
              }}
            >
              {inCart ? (
                <QtyRow
                  amount={cartAmount}
                  onIncrease={() => handleUpdateCart(cartAmount + 1)}
                  onDecrease={() => handleUpdateCart(cartAmount - 1)}
                  size="sm"
                />
              ) : (
                <div
                  onClick={handleAddToCart}
                  style={{ display: "flex", alignItems: "center", gap: 8, color: "white", fontWeight: 600, fontSize: 13, letterSpacing: 0.5, cursor: "pointer", userSelect: "none" }}
                >
                  <FiShoppingCart size={15} />
                  Add to Cart
                </div>
              )}
            </div>
          </div>

          {/* ── Info block ── */}
          <div className="product-card-info" style={{ padding: "13px 14px 15px", display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
              <p style={{ color: "#1a1a1a", fontSize: 14, fontWeight: 500, margin: 0, lineHeight: 1.4, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", flex: 1 }}>
                {product.name}
              </p>

              {/* Action buttons */}
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <IconBtn onClick={(e) => { e.stopPropagation(); setModalOpen(true); }} hoverColor="rgba(0,0,0,0.08)">
                  <FiEye size={15} style={{ color: "#666" }} />
                </IconBtn>

                {/* Favorite + login warning */}
                <div style={{ position: "relative" }}>
                  <button
                    onClick={handleFavoriteClick}
                    style={{
                      flexShrink: 0,
                      background: favorited ? "rgba(225,29,72,0.08)" : "rgba(0,0,0,0.04)",
                      border: `1px solid ${favorited ? "rgba(225,29,72,0.4)" : "rgba(0,0,0,0.1)"}`,
                      borderRadius: "50%", width: 32, height: 32,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      cursor: "pointer",
                      color: favorited ? "#e11d48" : "#999",
                      transition: "all 0.2s",
                    }}
                  >
                    <FiHeart size={15} fill={favorited ? "#e11d48" : "none"} stroke={favorited ? "#e11d48" : "currentColor"} />
                  </button>

                  {loginWarning && (
                    <div
                      style={{
                        position: "absolute", bottom: "calc(100% + 8px)", right: 0,
                        background: "#1a1a1a", color: "#fff", fontSize: 12, fontWeight: 500,
                        padding: "6px 10px", borderRadius: 8, whiteSpace: "nowrap",
                        pointerEvents: "none", zIndex: 100,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                      }}
                    >
                      Please login first
                      <div style={{ position: "absolute", bottom: -5, right: 10, width: 10, height: 10, background: "#1a1a1a", transform: "rotate(45deg)" }} />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Price row */}
            <div className="product-card-price-row" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ color: "#1a1a1a", fontSize: 16, fontWeight: 700, letterSpacing: 0.2 }}>
                ₪{product.price}
              </span>
              {product.hasDiscount && product.originalPrice && (
                <span style={{ color: "#bbb", fontSize: 13, textDecoration: "line-through" }}>
                  ₪{product.originalPrice}
                </span>
              )}
              <GenderBadge gender={product.gender} style={{ marginLeft: "auto" }} />
            </div>

            {/* Deal badge */}
            {product.deal && (
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <span style={{ background: "#7c3aed", color: "#fff", fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, whiteSpace: "nowrap" }}>
                  {product.deal}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {modalOpen && (
        <ProductModal
          product={product}
          onClose={() => setModalOpen(false)}
          onViewProduct={() => navigate(`/product/${product.id}`)}
          cartAmount={cartAmount}
          onAddToCart={handleAddToCart}
          onUpdateCart={handleUpdateCart}
        />
      )}
    </>
  );
}
